import React from 'react';
import PropTypes from 'prop-types';
import { graphql } from 'gatsby';
import Img from 'gatsby-image';
import styled from 'styled-components';
import breakpoint from 'styled-components-breakpoint';

import { hasCallToAction, makeImagePreviewable } from '../utils';

import Carousel from './Carousel';
import Block from './Block';
import Container from './Container';
import H3 from './H3';
import RichTextHtml from './RichTextHtml';
import CTAAnchor from './CTAAnchor';

const ImageWrapper = styled(Block)`
  height: 200px;
  overflow: hidden;
  position: relative;

  ${breakpoint('smallTablet')`
    width: 40%;
    height: 300px;
  `};

  ${breakpoint('tablet')`
    height: 400px
  `};
`;

const SlideImage = styled(Img)`
  position: absolute;
  top: 0;
  width: 100%;
  height: 100%;
`;

const SlideImageGif = styled.img`
  width: 100%;
  height: auto;
`;

const PageUSPCarousel = ({ content }) => (
  <Block position="relative" id={content.primary.internal_link_id}>
    <Container>
      <Carousel
        autoplay={Boolean(content.primary.autoplay)}
        slideInterval={content.primary.slide_interval || 10}
        dotColor="orange"
        dotsInsideCarousel
      >
        {content.items.map((item, i) => {
          const image = makeImagePreviewable(item.image);

          return (
            <Block
              key={`hero-item-${item.headline.text}-${i}`}
              style={{ minWidth: '100%' }}
              display={{ mobile: 'block', smallTablet: 'flex' }}
              alignItems="center"
            >
              <ImageWrapper px="baseUnit">
                {image.localFile.childImageSharp?.fluid ? (
                  <SlideImage
                    fluid={image.localFile.childImageSharp.fluid}
                    alt={image.alt}
                    imgStyle={{ objectFit: 'contain' }}
                  />
                ) : (
                  <Block
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    style={{ height: '100%' }}
                  >
                    <SlideImageGif src={image.localFile.url} />
                  </Block>
                )}
              </ImageWrapper>

              <Block style={{ flex: '1' }} p="baseUnit">
                <H3 color="blueDark" mb="baseUnit">
                  {item.headline.text}
                </H3>
                <RichTextHtml html={item.subtitle.html} />

                {hasCallToAction(item) ? (
                  <Block pt="baseUp4">
                    <CTAAnchor
                      text={item.call_to_action_text.text}
                      link={item.call_to_action_link}
                      colorTheme={item.call_to_action_color}
                    />
                  </Block>
                ) : null}
              </Block>
            </Block>
          );
        })}
      </Carousel>
    </Container>
  </Block>
);

PageUSPCarousel.propTypes = {
  content: PropTypes.shape({
    primary: PropTypes.shape({
      internal_link_id: PropTypes.string,
      autoplay: PropTypes.bool,
      slide_interval: PropTypes.number,
    }).isRequired,
    items: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  }).isRequired,
};

export default PageUSPCarousel;

export const query = graphql`
  fragment PageUSPCarouselFragment on PrismicPageBodyUspCarousel {
    id
    slice_type
    primary {
      internal_link_id
      autoplay
      slide_interval
    }
    items {
      headline {
        text
      }
      subtitle {
        html
      }
      image {
        alt
        localFile {
          url
          childImageSharp {
            fluid(maxWidth: 600) {
              ...GatsbyImageSharpFluid_withWebp_tracedSVG
            }
          }
        }
      }
      call_to_action_color
      call_to_action_text {
        text
      }
      call_to_action_link {
        link_type
        url
        lang
        target
      }
    }
  }
`;
